import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  Actions,
  ofActionCompleted,
  ofActionSuccessful,
  Store,
} from '@ngxs/store';
import { QuoteActions } from '@nibtravel/shared/data-access/quote';
import { UiContextActions } from '@nibtravel/shared/data-access/ui-context';
import { IRadioGroup } from '@nibtravel/shared/ui/forms/radio-group';
import { Qqc } from '@nibtravel/shared/util/e2e';
import {
  emptyPolicyHolder,
  IFormFieldValidation,
  IPolicyHolderAgeConfig,
} from '@nibtravel/shared/util/models';
import { PolicyHolderService } from '@nibtravel/shared/util/service';
import { merge } from 'lodash-es';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { AgeInputService } from './age-input.service';
import { AgeRadioService } from './age-radio.service';
import { TravellerAgeSelector } from './age-input.selector';
import { LoggerService } from '@nibtravel/shared/util/logger';

/** A wrapper that aggregates the logic regarding the traveller ages */
@Component({
  selector: 'nibtravel-qqc-age-input',
  templateUrl: './age-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QqcAgeInputComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClass = 'qqc-age-input';

  errors$ = new BehaviorSubject<string[]>([]);
  errorMap$ = new BehaviorSubject<Record<number, string[]>>({});
  destroy$ = new Subject<void>();
  isReady$ = new BehaviorSubject(false);

  /**
   * The form group input. This component is desined to be used within the QQC context
   * It needs to receive a form group so it can render and create the array of inputs
   *
   */
  @Input() qqcForm!: FormGroup;
  travellerConfig!: IRadioGroup;
  radioValidations!: IFormFieldValidation[];
  ageValidations!: IFormFieldValidation[];
  arrayValidations!: IFormFieldValidation[];
  policyHolderConfig!: IPolicyHolderAgeConfig;
  homeCountry!: string;
  e2eId = Qqc;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private actions: Actions,
    private logger: LoggerService,
    private policyHolderHelper: PolicyHolderService,
    private ageRadioService: AgeRadioService,
    private ageInputService: AgeInputService
  ) {}

  get policyHolders(): FormArray | undefined {
    return this.qqcForm
      ? (this.qqcForm.get('policyHolders') as FormArray)
      : undefined;
  }

  ngOnInit(): void {
    const config = this.store.select(TravellerAgeSelector.policyHolderConfig);
    const validations = this.ageInputService.getAgeInputValidations();
    const radioConfig = this.ageRadioService.getAgeRadioConfig();
    this.setupSubscriptions();

    combineLatest([config, validations, radioConfig])
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(([config, validations, radioConfig]) => {
        if (config) {
          this.arrayValidations = this.ageInputService.arrayValidation();
          this.policyHolderConfig = config;
          this.ageValidations = validations;
          this.travellerConfig = radioConfig;
          this.setForm();
          this.isReady$.next(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTravellerNumbersChange(travellerCount: number): void {
    this.travellerConfig.data.forEach(
      (item) => (item.checked = item.value == travellerCount)
    );
    const checked = this.travellerConfig.data.find((x) => x.checked === true);
    if (checked) {
      this.store.dispatch(
        new UiContextActions.SetTravellerAgeSelection(checked.inputId)
      );
    }
  }

  setAgePlaceholders(index: any) {
    let placeHolder;
    switch (index) {
      case 0:
        placeHolder = '32';
        break;
      case 1:
        placeHolder = '48';
        break;
      default:
        placeHolder = '8';
    }
    return placeHolder;
  }

  /**
   * Since this is an array group, we need to aggregate the messages.
   * Otherwise, they get overriden depending on the state of each field.
   * We might be able to abstract this logic to a utility helper later on.
   *
   */
  onError(errors: string[], policyHolderIndex: number): void {
    const current = this.errorMap$.value;
    const received = { [policyHolderIndex]: errors };
    const next: Record<number, string[]> = { ...current, ...received };
    this.errorMap$.next(next);

    // Now we combine all existing arrays and transform into single array
    // so the message component can render the errors
    this.flattenErrorArray(next);
  }

  private flattenErrorArray(next: Record<number, string[]>) {
    let result: string[] = [];
    Object.keys(next).forEach((key) => {
      const errors = next[Number.parseInt(key)];
      if (errors.length) {
        result = [...result, ...errors];
      }
    });

    this.errors$.next(result);
  }

  private setForm(): void {
    if (this.qqcForm) {
      this.qqcForm.addControl('policyHolders', this.fb.array([]));
      const hasChecked = this.travellerConfig.data.filter((r) => r.checked);
      if (hasChecked.length > 1) {
        throw new Error(
          'Incorrect radio group config. Should not have more than one radio checked'
        );
      }

      if (hasChecked.length) {
        const checked = hasChecked[0];
        this.onTravellerNumbersChange(checked.value);
      }
    }
  }

  private policyHolderControls(
    initialAge?: number | null
  ): Record<string, unknown> {
    let group = {};
    Object.keys(emptyPolicyHolder).forEach((key) => {
      const control =
        initialAge != null && key === 'age'
          ? this.fb.control(initialAge)
          : this.fb.control(emptyPolicyHolder[key]);
      group = { ...group, [key]: control };
    });

    return group;
  }

  private setupSubscriptions(): void {
    merge(
      this.actions.pipe(ofActionCompleted(QuoteActions.UpdatePolicyholder)),
      this.actions.pipe(ofActionCompleted(QuoteActions.UpdateAmbiguity))
    )
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(this.checkTravellerCount);

    this.actions
      .pipe(
        ofActionSuccessful(UiContextActions.SetTravellerAgeSelection),
        distinctUntilChanged(),
        switchMap(() =>
          this.store.select(TravellerAgeSelector.travellerSelection)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((selection) => this.setAgeInputs(selection));
  }

  private setAgeInputs(inputId: string) {
    const checked = this.travellerConfig.data.find(
      (x) => x.inputId === inputId
    );
    if (
      !checked ||
      !this.policyHolders ||
      this.policyHolders.length === checked.value
    ) {
      return;
    }

    if (this.policyHolders.length > checked.value) {
      while (this.policyHolders.length != checked.value) {
        const index = this.policyHolders.length - 1;
        this.policyHolders.removeAt(index);
        const errorMap = this.errorMap$.value;
        if (errorMap) {
          delete errorMap[index];
          this.errorMap$.next(errorMap);
        }
      }

      this.flattenErrorArray(this.errorMap$.value);
    } else {
      let index = this.policyHolders.length || 0;
      const initialState = this.store.selectSnapshot(
        TravellerAgeSelector.policyHolders
      );
      while (this.policyHolders.length != checked.value) {
        const initialValue = initialState[index]
          ? initialState[index].age
          : null;
        const policyHolder = this.fb.group(
          this.policyHolderControls(initialValue)
        );

        this.policyHolders.push(policyHolder);
        index++;
      }
    }
  }

  private checkTravellerCount = () => {
    if (this.policyHolderConfig) {
      // check count validity when policyholders are updated
      const validCountMsg = this.policyHolderHelper.countError(
        this.policyHolders?.value,
        this.policyHolderConfig
      );
      if (validCountMsg) {
        const errors = [validCountMsg];
        this.errors$.next(errors);
      }
    } else {
      this.logger.error({
        value: 'PolicyHolder config is not yet defined',
        category: 'QqcTravellerAgeComponent',
        method: 'QqcTravellerAgeComponent.checkTravellerCount',
        context: {
          message: 'policyHolderConfig is not defined',
        },
      });
    }
  };
}
